import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Laptop, Loader2, LogOut, Smartphone } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { errMessage } from "@/lib/api";

const MOBILE = /iPhone|iPad|Android/;

function ago(iso) {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 5) return "active now";
  if (mins < 60) return `last active ${mins} min ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `last active ${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.round(h / 24);
  return `last active ${d} day${d === 1 ? "" : "s"} ago`;
}

function SessionRow({ s, onEnd }) {
  const Icon = MOBILE.test(s.device) ? Smartphone : Laptop;
  return (
    <li className="flex items-center justify-between gap-3 py-3" data-testid={`session-${s.id}`}>
      <div className="flex min-w-0 items-center gap-3">
        <Icon className="h-5 w-5 shrink-0 text-slate-400" />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-slate-900" data-testid={`session-device-${s.id}`}>
            {s.device}{s.kind === "google" && <span className="font-normal text-slate-500"> · Google</span>}
          </p>
          <p className="text-xs text-slate-500">{s.current ? "This device" : ago(s.last_active)} · signed in {new Date(s.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</p>
        </div>
      </div>
      {s.current ? <Badge variant="secondary" data-testid="session-current">Current</Badge> : (
        <Button size="sm" variant="outline" onClick={() => onEnd(s.id)} data-testid={`session-end-${s.id}`}><LogOut className="mr-1.5 h-3.5 w-3.5" />Sign out</Button>
      )}
    </li>
  );
}

export function ActiveSessions() {
  const [sessions, setSessions] = useState(null);
  const load = useCallback(() => api.get("/auth/sessions").then((r) => setSessions([...r.data].sort((x, y) => y.current - x.current))).catch(() => setSessions([])), []);
  useEffect(() => { load(); }, [load]);

  const end = async (id) => {
    try { await api.delete(`/auth/sessions/${id}`); toast.success("Device signed out."); load(); } catch (e) { toast.error(errMessage(e)); }
  };
  const endOthers = async () => {
    try { const r = await api.post("/auth/sessions/revoke-others"); toast.success(`Signed out ${r.data.signed_out} other device${r.data.signed_out === 1 ? "" : "s"}.`); load(); } catch (e) { toast.error(errMessage(e)); }
  };
  const others = (sessions || []).filter((s) => !s.current).length;

  return (
    <Card className="mt-6 border-slate-200 shadow-sm" data-testid="sessions-card">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Signed-in devices</h2>
            <p className="mt-1 text-sm text-slate-600">Sign out any device you don't recognise.</p>
          </div>
          {others > 0 && <Button size="sm" variant="outline" onClick={endOthers} data-testid="sessions-end-others">Sign out all other devices</Button>}
        </div>
        {!sessions ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#0D9488]" /></div> : (
          <ul className="mt-3 divide-y divide-slate-100" data-testid="sessions-list">
            {sessions.map((s) => <SessionRow key={s.id} s={s} onEnd={end} />)}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
